import { Fragment, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    PiArrowSquareOutBold,
    PiBellBold,
    PiCaretDownBold,
    PiCaretRightBold,
    PiCheckBold,
    PiFilePdfBold,
    PiMagnifyingGlassBold,
    PiWarningCircleBold,
} from 'react-icons/pi';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import { LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import type { PaymentApprovalListResponse, PaymentApprovalSellerSummary } from './types';
import MonthClosingCard from './MonthClosingCard';

function fmt(v: number) {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
}

function fmtPct(v: number | null | undefined) {
    if (v == null || Number.isNaN(v)) return '—';
    return `${v.toFixed(2)}%`;
}

function fmtPeriod(ym: string) {
    const [y, m] = ym.split('-');
    return `${m}/${y}`;
}

function currentPeriod() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

const STATUS_PILL: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-700',
    awaiting_nf: 'bg-amber-100 text-amber-800',
    nf_submitted: 'bg-blue-100 text-blue-800',
    paid: 'bg-emerald-100 text-emerald-800',
};

function StatusPills({ counts }: { counts: PaymentApprovalSellerSummary['status_counts'] }) {
    const items = [
        { key: 'pending', label: 'Pendente', n: counts.pending },
        { key: 'awaiting_nf', label: 'Aguard. NF', n: counts.awaiting_nf },
        { key: 'nf_submitted', label: 'NF Enviada', n: counts.nf_submitted },
        { key: 'paid', label: 'Paga', n: counts.paid },
    ].filter((i) => i.n > 0);

    if (items.length === 0) {
        return <span className="text-xs text-gray-400">—</span>;
    }

    return (
        <div className="flex flex-wrap gap-1">
            {items.map((i) => (
                <span
                    key={i.key}
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[i.key]}`}
                >
                    {i.n} {i.label}
                </span>
            ))}
        </div>
    );
}

function SellerDrilldown({
    seller,
    period,
    onApprove,
    onRequestNf,
    approving,
    requesting,
}: {
    seller: PaymentApprovalSellerSummary;
    period: string;
    onApprove: () => void;
    onRequestNf: () => void;
    approving: boolean;
    requesting: boolean;
}) {
    return (
        <div className="space-y-3 bg-gray-50/80 px-4 py-4 dark:bg-zinc-900/40">
            {!seller.totals_match && (
                <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    <PiWarningCircleBold className="mt-0.5 size-4 shrink-0" />
                    <span>
                        Totais da NF divergem da soma das vendas
                        {seller.mismatch_amount !== 0 ? ` (${fmt(Math.abs(seller.mismatch_amount))})` : ''}.
                        Recalcule antes de aprovar.
                    </span>
                </div>
            )}

            {seller.unlinked_records_count > 0 && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <PiBellBold className="mt-0.5 size-4 shrink-0" />
                    <span>
                        {seller.unlinked_records_count} venda(s) ainda sem NF mensal
                        ({fmt(seller.unlinked_commissions)}). Use “Pedir NF” para consolidar.
                    </span>
                </div>
            )}

            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                <table className="w-full min-w-[960px] text-sm">
                    <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                        <tr>
                            <th className="px-3 py-2.5 text-left">Data</th>
                            <th className="px-3 py-2.5 text-left">Tipo</th>
                            <th className="px-3 py-2.5 text-left">Deal</th>
                            <th className="px-3 py-2.5 text-left">Cliente</th>
                            <th className="px-3 py-2.5 text-right">Valor base</th>
                            <th className="px-3 py-2.5 text-right">%</th>
                            <th className="px-3 py-2.5 text-right">Comissão</th>
                            <th className="px-3 py-2.5 text-center">Status</th>
                            <th className="px-3 py-2.5 text-right">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {seller.records.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-4 py-8 text-center text-gray-400">
                                    Nenhuma venda neste período.
                                </td>
                            </tr>
                        ) : (
                            seller.records.map((r) => (
                                <tr
                                    key={r.id}
                                    className={r.confidential ? 'bg-red-50/40' : 'hover:bg-gray-50 dark:hover:bg-zinc-800/50'}
                                >
                                    <td className="whitespace-nowrap px-3 py-2.5 text-xs text-gray-500">
                                        {r.date ?? '—'}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <div className="flex flex-col gap-1">
                                            <span className="w-fit rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-800">
                                                {r.role_label}
                                            </span>
                                            {r.installment_number != null && (
                                                <span className="text-[11px] text-red-600">
                                                    Parcela {r.installment_number}
                                                    {r.total_installments ? `/${r.total_installments}` : ''}
                                                </span>
                                            )}
                                            {r.mode_flags.length > 0 && (
                                                <div className="flex flex-wrap gap-1">
                                                    {r.mode_flags.map((f) => (
                                                        <span
                                                            key={f.key}
                                                            className="rounded bg-violet-50 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
                                                        >
                                                            {f.label}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <p className="font-semibold text-gray-900 dark:text-zinc-100">
                                            {r.deal_number ?? `#${r.ploomes_deal_id}`}
                                        </p>
                                        {r.deal_title && (
                                            <p className="max-w-[200px] truncate text-xs text-gray-400">{r.deal_title}</p>
                                        )}
                                    </td>
                                    <td className="max-w-[160px] truncate px-3 py-2.5 text-xs text-gray-600">
                                        {r.client_name ?? '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-gray-700">
                                        {fmt(r.base_amount)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-right text-gray-600">
                                        {fmtPct(r.rate_pp)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2.5 text-right font-bold text-emerald-700">
                                        {fmt(r.final_amount)}
                                    </td>
                                    <td className="px-3 py-2.5 text-center">
                                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_PILL[r.status] ?? 'bg-gray-100 text-gray-600'}`}>
                                            {r.status_label}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        {r.deal_url ? (
                                            <Link
                                                to={r.deal_url}
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"
                                            >
                                                Ver deal
                                                <PiArrowSquareOutBold className="size-3.5" />
                                            </Link>
                                        ) : (
                                            <span className="text-xs text-gray-300">—</span>
                                        )}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-gray-500">
                    Base {fmt(seller.total_base)}
                    {seller.avg_rate_pp != null && <> · média {fmtPct(seller.avg_rate_pp)}</>}
                    {' · '}
                    comissão <span className="font-semibold text-emerald-700">{fmt(seller.total_commissions)}</span>
                    {seller.total_salary > 0 && <> · salário {fmt(seller.total_salary)}</>}
                </div>
                <div className="flex flex-wrap gap-2">
                    {seller.invoice_url && (
                        <a
                            href={seller.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
                        >
                            <PiFilePdfBold className="size-3.5" />
                            Ver NF
                        </a>
                    )}
                    {(seller.nf_status === 'awaiting_nf' || seller.nf_status === 'unconsolidated' || seller.unlinked_records_count > 0) && (
                        <button
                            type="button"
                            onClick={onRequestNf}
                            disabled={requesting}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50"
                        >
                            <PiBellBold className="size-3.5" />
                            Pedir NF
                        </button>
                    )}
                    {seller.approvable_nf_ids.length > 0 && (
                        <button
                            type="button"
                            onClick={onApprove}
                            disabled={approving || !seller.totals_match}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                        >
                            <PiCheckBold className="size-3.5" />
                            Aprovar {fmt(seller.total_amount)}
                        </button>
                    )}
                    <Link
                        to={`/commissioning/payment-approvals/${seller.user_id}?period=${period || seller.periods[0] || currentPeriod()}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                    >
                        Página completa
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default function CommissionPaymentApprovalPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    // null na URL = default mês atual; period="" = todos os períodos
    const periodParam = searchParams.get('period');
    const period = periodParam === null ? currentPeriod() : periodParam;
    const qc = useQueryClient();
    const [expanded, setExpanded] = useState<number | null>(null);
    const [search, setSearch] = useState('');
    const [confirmSeller, setConfirmSeller] = useState<PaymentApprovalSellerSummary | null>(null);

    const { data, isLoading } = useQuery<PaymentApprovalListResponse>({
        queryKey: ['commissioning', 'payment-approvals', period || 'all'],
        queryFn: () => api.get('/commissioning/payment-approvals', {
            params: period ? { period } : {},
        }).then((r) => r.data),
    });

    const requestNfMut = useMutation({
        mutationFn: (userId: number) =>
            api.post(`/commissioning/payment-approvals/${userId}/request-nf`, {}),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'NF solicitada!');
            qc.invalidateQueries({ queryKey: ['commissioning'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Erro ao solicitar NF.'),
    });

    const approveMut = useMutation({
        mutationFn: (seller: PaymentApprovalSellerSummary) =>
            api.post(`/commissioning/payment-approvals/${seller.user_id}/approve`, {
                monthly_nf_ids: seller.approvable_nf_ids,
            }),
        onSuccess: (res) => {
            toast.success(res.data.message ?? 'Pagamento aprovado!');
            setConfirmSeller(null);
            qc.invalidateQueries({ queryKey: ['commissioning'] });
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? 'Erro ao aprovar.'),
    });

    const sellers = data?.data ?? [];
    const meta = data?.meta;

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return sellers;
        return sellers.filter((s) =>
            s.user_name.toLowerCase().includes(q)
            || s.user_email.toLowerCase().includes(q)
            || (s.job_title ?? '').toLowerCase().includes(q),
        );
    }, [sellers, search]);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Aprovação de Pagamento"
                subtitle="Fila do RH — expandir o vendedor para conferir todas as vendas do período"
            />

            <MonthClosingCard />

            <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-rose-100 bg-rose-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-rose-700">Total de registros</p>
                    <p className="mt-1 text-2xl font-black text-rose-950">{meta?.total_records ?? 0}</p>
                </div>
                <div className="rounded-xl border border-amber-100 bg-amber-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Total base</p>
                    <p className="mt-1 text-2xl font-black text-amber-950">{fmt(meta?.total_base ?? 0)}</p>
                </div>
                <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Total de comissões</p>
                    <p className="mt-1 text-2xl font-black text-emerald-950">{fmt(meta?.total_commissions ?? 0)}</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
                <label className="text-sm font-medium text-gray-600">Período:</label>
                <select
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none"
                    value={period}
                    onChange={(e) => setSearchParams({ period: e.target.value })}
                >
                    <option value="">Todos os períodos</option>
                    {(meta?.available_periods ?? []).map((p) => (
                        <option key={p} value={p}>{fmtPeriod(p)}</option>
                    ))}
                </select>

                <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
                    <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Vendedor, e-mail…"
                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm shadow-sm focus:border-primary focus:outline-none"
                    />
                </div>
            </div>

            {(meta?.awaiting_nf_count ?? 0) > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start gap-3">
                        <PiBellBold className="mt-0.5 size-5 shrink-0 text-amber-600" />
                        <div>
                            <p className="font-semibold text-amber-900">
                                {meta?.awaiting_nf_sellers} vendedor(es) ainda não enviaram a NF
                            </p>
                            <p className="mt-0.5 text-sm text-amber-700">
                                {meta?.awaiting_nf_count} NF(s) totalizando {fmt(meta?.awaiting_nf_amount ?? 0)} aguardando envio.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {isLoading ? (
                <LoadingState variant="table" />
            ) : (
                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-900">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] text-sm">
                            <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                                <tr>
                                    <th className="w-10 px-3 py-3" />
                                    <th className="px-3 py-3 text-left">Vendedor</th>
                                    <th className="px-3 py-3 text-left">Resumo</th>
                                    <th className="px-3 py-3 text-right">Valor base</th>
                                    <th className="px-3 py-3 text-right">%</th>
                                    <th className="px-3 py-3 text-right">Comissão</th>
                                    <th className="px-3 py-3 text-left">NF / comprovante</th>
                                    <th className="px-3 py-3 text-left">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                {filtered.length === 0 && (
                                    <tr>
                                        <td colSpan={8} className="px-6 py-16 text-center text-gray-400">
                                            Nenhuma comissão pendente neste período.
                                        </td>
                                    </tr>
                                )}
                                {filtered.map((s) => {
                                    const open = expanded === s.user_id;
                                    return (
                                        <Fragment key={s.user_id}>
                                            <tr
                                                className={`cursor-pointer transition-colors ${open ? 'bg-sky-50/60 dark:bg-sky-950/20' : 'hover:bg-gray-50 dark:hover:bg-zinc-800/40'}`}
                                                onClick={() => setExpanded(open ? null : s.user_id)}
                                            >
                                                <td className="px-3 py-3 text-gray-400">
                                                    {open
                                                        ? <PiCaretDownBold className="size-4" />
                                                        : <PiCaretRightBold className="size-4" />}
                                                </td>
                                                <td className="px-3 py-3">
                                                    <div className="flex items-center gap-3">
                                                        {s.user_avatar_url ? (
                                                            <img
                                                                src={s.user_avatar_url}
                                                                alt={s.user_name}
                                                                className="size-10 rounded-full object-cover ring-2 ring-gray-100"
                                                            />
                                                        ) : (
                                                            <span className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-500">
                                                                {s.user_name.slice(0, 2).toUpperCase()}
                                                            </span>
                                                        )}
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-gray-900 dark:text-zinc-100">{s.user_name}</p>
                                                            <p className="truncate text-xs text-gray-500">{s.user_email}</p>
                                                        </div>
                                                        {!s.totals_match && (
                                                            <span title="Totais divergentes">
                                                                <PiWarningCircleBold className="size-4 shrink-0 text-red-500" />
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-3">
                                                    <p className="text-xs text-gray-500">Total valor base</p>
                                                    <p className="font-semibold text-gray-900">{fmt(s.total_base)}</p>
                                                    <span className="mt-1 inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
                                                        {s.records_count} processo(s)
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-right font-medium text-gray-800">
                                                    {fmt(s.total_base)}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-right text-gray-600">
                                                    <span className="text-[10px] uppercase text-gray-400">Média</span>
                                                    <p>{fmtPct(s.avg_rate_pp)}</p>
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-3 text-right">
                                                    <p className="text-base font-black text-emerald-700">{fmt(s.total_commissions)}</p>
                                                    {s.total_salary > 0 && (
                                                        <p className="text-[11px] text-gray-400">+ salário {fmt(s.total_salary)}</p>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                                    {s.invoice_url ? (
                                                        <a
                                                            href={s.invoice_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"
                                                        >
                                                            <PiFilePdfBold className="size-3.5" />
                                                            Ver comprovantes
                                                        </a>
                                                    ) : (
                                                        <span className="text-xs text-gray-400">
                                                            {s.nf_status === 'nf_submitted' ? 'NF enviada' : 'Sem comprovante'}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                                                    <StatusPills counts={s.status_counts} />
                                                </td>
                                            </tr>
                                            {open && (
                                                <tr>
                                                    <td colSpan={8} className="p-0">
                                                        <SellerDrilldown
                                                            seller={s}
                                                            period={period}
                                                            approving={approveMut.isPending}
                                                            requesting={requestNfMut.isPending}
                                                            onApprove={() => setConfirmSeller(s)}
                                                            onRequestNf={() => requestNfMut.mutate(s.user_id)}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <ConfirmModal
                open={!!confirmSeller}
                onClose={() => setConfirmSeller(null)}
                onConfirm={() => confirmSeller && approveMut.mutate(confirmSeller)}
                title="Confirmar pagamento"
                description={
                    confirmSeller
                        ? `Aprovar ${confirmSeller.approvable_nf_ids.length} NF(s) de ${confirmSeller.user_name}? Total: ${fmt(confirmSeller.total_amount)}${confirmSeller.total_salary > 0 ? ` (inclui salário ${fmt(confirmSeller.total_salary)})` : ''}.`
                        : ''
                }
                confirmLabel="Sim, aprovar"
                confirmClass="bg-emerald-600 hover:bg-emerald-700"
            />
        </div>
    );
}
